import { memo, useState, useCallback } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { NodeData, STEP_CONFIG } from '@/types/node';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp, Check, Circle } from 'lucide-react';

interface StepNodeProps extends NodeProps {
  data: NodeData;
}

export const StepNode = memo(({ data, selected }: StepNodeProps) => {
  const [isExpanded, setIsExpanded] = useState(data.isExpanded);

  const config = STEP_CONFIG[data.stepType];
  const completionRate = data.requiredFields.length > 0
    ? (data.completedFields.length / data.requiredFields.length) * 100
    : 0;

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  return (
    <div className="step-node">
      {/* 입력 핸들 (왼쪽) */}
      {data.stepType > 0 && (
        <Handle
          type="target"
          position={Position.Left}
          className="w-3 h-3 !bg-gray-400"
        />
      )}

      <Card
        className={`
          w-[300px] transition-all duration-200 cursor-pointer
          ${selected ? 'ring-2 ring-primary shadow-lg' : 'shadow-md'}
          ${data.isCompleted ? 'border-green-500' : 'border-gray-300'}
        `}
        style={{ borderTopColor: config.color, borderTopWidth: '4px' }}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                style={{ backgroundColor: config.color + '20', color: config.color }}
              >
                Step {data.stepType}
              </Badge>
              {data.isCompleted && (
                <Check className="w-4 h-4 text-green-500" />
              )}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggle}
              className="h-6 w-6 p-0"
            >
              {isExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </Button>
          </div>
          <CardTitle className="text-sm font-semibold mt-1">
            {config.name}
          </CardTitle>
          {data.label && (
            <p className="text-xs text-muted-foreground mt-1">{data.label}</p>
          )}
        </CardHeader>

        {isExpanded && (
          <CardContent className="pt-0">
            {/* 진행률 */}
            <div className="mb-3">
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                <span>진행률</span>
                <span>{Math.round(completionRate)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="h-2 rounded-full transition-all"
                  style={{
                    width: `${completionRate}%`,
                    backgroundColor: config.color,
                  }}
                />
              </div>
            </div>

            {/* 필수 항목 체크리스트 */}
            <div className="space-y-1">
              <p className="text-xs font-medium mb-2">필수 항목</p>
              {data.requiredFields.map((field) => {
                const isCompleted = data.completedFields.includes(field);
                return (
                  <div
                    key={field}
                    className="flex items-center gap-2 text-xs"
                  >
                    {isCompleted ? (
                      <Check className="w-3 h-3 text-green-500" />
                    ) : (
                      <Circle className="w-3 h-3 text-gray-300" />
                    )}
                    <span className={isCompleted ? 'line-through text-muted-foreground' : ''}>
                      {field}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* 설명 */}
            {data.description && (
              <p className="text-xs text-muted-foreground mt-3 border-t pt-2">
                {data.description}
              </p>
            )}
          </CardContent>
        )}
      </Card>

      {/* 출력 핸들 (오른쪽) */}
      {data.stepType < 10 && (
        <Handle
          type="source"
          position={Position.Right}
          className="w-3 h-3 !bg-gray-400"
        />
      )}
    </div>
  );
});

StepNode.displayName = 'StepNode';
